/**
 * Individual Notification Item with smooth animations.
 * - Slides down from top on enter
 * - Fades out + collapses height on dismiss
 * - Progress bar perfectly inset within the rounded card
 */

import { useEffect, useRef, useState, useCallback } from "react";
import type { Notification } from "../../services/notification.types";
import { NotificationIcon, NOTIFICATION_STYLES } from "./NotificationIcons";

interface NotificationItemProps {
 notification: Notification;
 onDismiss: (id: string) => void;
 onAction?: (url: string) => void;
}

export default function NotificationItem({ notification, onDismiss, onAction }: NotificationItemProps) {
 const [phase, setPhase] = useState<"entering" | "visible" | "exiting">("entering");
 const [height, setHeight] = useState<number |"auto">("auto");
 const [progress, setProgress] = useState(100);
 const contentRef = useRef<HTMLDivElement>(null);
 const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
 const startTimeRef = useRef(Date.now());
 const pausedRef = useRef(false);
 const remainingRef = useRef(notification.duration);

 const style = NOTIFICATION_STYLES[notification.type];

 // Enter animation — slight delay for smooth stagger
 useEffect(() => {
 const raf = requestAnimationFrame(() => {
 requestAnimationFrame(() => {
 setPhase("visible");
 });
 });
 return () => cancelAnimationFrame(raf);
 }, []);

 // Auto-dismiss timer with smooth progress
 useEffect(() => {
 if (notification.duration === 0) return;

 startTimeRef.current = Date.now();
 remainingRef.current = notification.duration;

 timerRef.current = setInterval(() => {
 if (pausedRef.current) return;

 const elapsed = Date.now() - startTimeRef.current;
 const remaining = remainingRef.current - elapsed;
 const pct = Math.max(0, (remaining / notification.duration) * 100);
 setProgress(pct);

 if (remaining <= 0) {
 if (timerRef.current) clearInterval(timerRef.current);
 handleDismiss();
 }
 }, 30);

 return () => {
 if (timerRef.current) clearInterval(timerRef.current);
 };
 }, [notification.duration]);

 const handleMouseEnter = useCallback(() => {
 pausedRef.current = true;
 const elapsed = Date.now() - startTimeRef.current;
 remainingRef.current = Math.max(0, remainingRef.current - elapsed);
 }, []);

 const handleMouseLeave = useCallback(() => {
 pausedRef.current = false;
 startTimeRef.current = Date.now();
 }, []);

 const handleDismiss = useCallback(() => {
 if (phase ==="exiting") return;

 // Capture current height for collapse animation
 if (contentRef.current) {
 setHeight(contentRef.current.offsetHeight);
 }

 // Start exit — two-frame to trigger transition
 requestAnimationFrame(() => {
 requestAnimationFrame(() => {
 setPhase("exiting");
 setHeight(0);
 });
 });

 // Remove after animation completes
 setTimeout(() => {
 onDismiss(notification.id);
 }, 400);
 }, [phase, notification.id, onDismiss]);

 const handleAction = useCallback(() => {
 if (notification.actionUrl && onAction) {
 onAction(notification.actionUrl);
 }
 handleDismiss();
 }, [notification.actionUrl, onAction, handleDismiss]);

 const timeAgo = formatTimeAgo(notification.timestamp);

 return (
 <div
 role="alert"aria-live="polite"aria-atomic="true"tabIndex={0}
 onKeyDown={(e) => {
 if (e.key ==="Escape") handleDismiss();
 }}
 onMouseEnter={handleMouseEnter}
 onMouseLeave={handleMouseLeave}
 onFocus={handleMouseEnter}
 onBlur={handleMouseLeave}
 className="focus:outline-none"style={{
 overflow: "hidden",
 maxHeight: phase ==="exiting"?"0px": height ==="auto"?"200px":`${height}px`,
 opacity: phase ==="entering"? 0 : phase ==="exiting"? 0 : 1,
 transform:
 phase ==="entering"?"translateY(-12px) scale(0.98)": phase ==="exiting"?"translateY(-8px) scale(0.96)":"translateY(0) scale(1)",
 marginBottom: phase ==="exiting"?"0px":"8px",
 transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
 }}
 >
 <div
 ref={contentRef}
 className={`relative overflow-hidden rounded-lg border shadow-md ${style.bgColor} ${style.borderColor}`}
 style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.04)"}}
 >
 {/* Left accent bar */}
 <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-lg ${style.progressColor}`} />

 {/* Main content */}
 <div className="flex items-start gap-3 pl-5 pr-4 py-3">
 {/* Icon */}
 <div className="mt-0.5 shrink-0">
 <NotificationIcon type={notification.type} className="h-[18px] w-[18px]"/>
 </div>

 {/* Text */}
 <div className="flex-1 min-w-0">
 <div className="flex items-start justify-between gap-2">
 <p className="text-[13px] font-semibold text-slate-800 leading-snug">
 {notification.title}
 </p>
 <button
 type="button"onClick={handleDismiss}
 aria-label="Dismiss notification"className="shrink-0 -mt-0.5 -mr-1 rounded-md p-1 text-slate-400 transition-colors hover:bg-black/5 hover:text-slate-600">
 <svg className="h-3.5 w-3.5"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2.5"strokeLinecap="round"strokeLinejoin="round">
 <line x1="18"y1="6"x2="6"y2="18"/>
 <line x1="6"y1="6"x2="18"y2="18"/>
 </svg>
 </button>
 </div>

 {notification.message && (
 <p className="mt-0.5 text-[11px] text-slate-600 leading-relaxed line-clamp-2">
 {notification.message}
 </p>
 )}

 <div className="mt-1.5 flex items-center gap-3">
 <span className="text-[10px] text-slate-400">
 {timeAgo}
 </span>

 {notification.actionUrl && notification.actionLabel && (
 <button
 type="button"onClick={handleAction}
 className="text-[10px] font-semibold text-role-primary hover:underline">
 {notification.actionLabel}
 </button>
 )}
 </div>
 </div>
 </div>

 {/* Progress bar — perfectly inset at the bottom of the rounded card */}
 {notification.duration > 0 && (
 <div className="h-[3px] w-full bg-black/[0.03]">
 <div
 className={`h-full ${style.progressColor}`}
 style={{
 width:`${progress}%`,
 opacity: 0.7,
 borderRadius: progress < 100 ?"0 2px 2px 0": undefined,
 transition: "width 0.06s linear",
 }}
 />
 </div>
 )}
 </div>
 </div>
 );
}

function formatTimeAgo(timestamp: number): string {
 const diff = Date.now() - timestamp;
 const seconds = Math.floor(diff / 1000);
 if (seconds < 5) return"Just now";
 if (seconds < 60) return`${seconds}s ago`;
 const minutes = Math.floor(seconds / 60);
 if (minutes < 60) return`${minutes}m ago`;
 const hours = Math.floor(minutes / 60);
 if (hours < 24) return`${hours}h ago`;
 return`${Math.floor(hours / 24)}d ago`;
}
